import type { FormikHelpers } from 'formik';
import { ReactNode } from 'react';
import { useLogger, useServices } from '@packages/shared/core/common/hooks';
import { useQuizStep } from '@packages/shared/core/quiz/hooks';
import type { NameStepData, QuizScreenProps } from '@packages/shared/core/quiz/entities';
import { ApiError } from '@packages/shared/core/common/errors';

type NameQuizStepProps = {
  id: string;
  renderQuizScreen: (stepData: QuizScreenProps) => ReactNode;
};

const NameQuizStep = ({ id, renderQuizScreen }: NameQuizStepProps) => {
  const logger = useLogger();
  const { back, next, stepData, updateUserPreferences, userPreferences } = useQuizStep(id);

  const { userService } = useServices();

  if (!stepData) {
    // TODO add fallback
    logger.error(new TypeError(`Data for ${id} step not found`));
    return null;
  }

  const { dataKey } = stepData as NameStepData;

  const initialValue = typeof userPreferences[dataKey] === 'string' ? (userPreferences[dataKey] as string) : '';

  const goBack = async () => back();

  const onSubmit = async (name: string, { setErrors }: FormikHelpers<{ name: string }>) => {
    updateUserPreferences(dataKey, name);
    try {
      await userService.updateUserInformation({ name });
      await next();
    } catch (err) {
      if (err instanceof ApiError) {
        setErrors({ name: err.originalError.message });
      } else {
        setErrors({ name: 'Something went wrong. Try again later' });
      }
    }
  };

  return (
    <>
      {renderQuizScreen({
        initialValue,
        onSubmit,
        onBack: goBack,
        ...(stepData as NameStepData),
      })}
    </>
  );
};

export default NameQuizStep;
